class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.initialResultsMarkup = this.predictiveSearchResults?.innerHTML || '';
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.numberOfResults = this.getAttribute('data-number-of-results') || 5;
    this.viewAllResults = document.getElementById('predictive-search-view-all-result');
    this.searchReultsExample = document.querySelectorAll('.predictive-search__results-groups-wrapper');
    
    this.searchModal = this.closest('modal-dialog');
    this.searchItemsInMobileDrawer = this.querySelector('.search-items-mobile-drawer');
    
    this.clearButton = this.querySelector('.clear-button');
    if (this.clearButton) this.clearButton.addEventListener('click', this.clearSearch.bind(this));

    if (!this.input) return;
    this.setupEventListeners();
    if (this.searchModal) this.searchModal.addEventListener('modalChanged', debounce(this.onModalChanged.bind(this), 0));
  }

  init () {
    this.id = this.getAttribute('id')
    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    
    if (this.input) {
      this.input?.form?.addEventListener('reset', this.onFormReset.bind(this));
      this.input?.addEventListener(
        'input',
        debounce((event) => {
          this.onChange(event);
        }, 300).bind(this)
      );
    }

    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.initialResultsMarkup = this.predictiveSearchResults?.innerHTML || '';
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.numberOfResults = this.getAttribute('data-number-of-results') || 5;
    this.viewAllResults = document.getElementById('predictive-search-view-all-result');
    this.searchReultsExample = document.querySelectorAll('.predictive-search__results-groups-wrapper');
    this.searchModal = this.closest('modal-dialog');
    this.searchItemsInMobileDrawer = this.querySelector('.search-items-mobile-drawer');

    if (!this.input) return;
    if (this.searchModal) this.searchModal.addEventListener('modalChanged', debounce(this.onModalChanged.bind(this), 0));
    this.setupEventListeners();
  }

  clearSearch(e) {
    e.stopPropagation();
    e.preventDefault();
    this.input.value = '';
    this.onFormReset(e);
    const dropdown = e.target.closest('insp-dropdown');
    if (dropdown) dropdown.closePopover(e);
  }

  setupEventListeners() {
    if (this.input) this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    if (this.input) this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  onModalChanged(event) {
    if (event.detail && !event.detail.show) {
      // Modal closing
      if (this.input?.closest('.menu-drawer')) {
        const menuDrawer = this.input.closest('.menu-drawer');
        menuDrawer.querySelector('.menu-drawer__inner-container').classList.remove('show-search-items');
      }
      this.close(true);
    } else if (event.detail && event.detail.show) {
      // ✅ Modal opening - ensure recommendations are accessible
      const isInSearchModal = this.searchModal && this.searchModal.id === 'modal-search';
      
      if (isInSearchModal) {
        // Force open state so Tab navigation works
        this.setAttribute('open', 'true');
        if (this.input) this.input.setAttribute('aria-expanded', 'true');
        this.isOpen = true;
        
        // Show recommendations
        this.searchReultsExample.forEach(element => {
          element.classList.remove('hidden');
          element.classList.add('flex');
        });
      }
    }
  }

  getQuery() {
    return this.input?.value?.trim() || '';
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.clearResults();
    }

    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;

    // ✅ MODIFIED: Don't close when empty if in search modal
    if (!this.searchTerm.length) {
      const isInSearchModal = this.searchModal && this.searchModal.id === 'modal-search';
      
      if (isInSearchModal) {
        // Keep modal open with recommendations accessible
        this.clearResults();
        this.removeAttribute('results');
        if (this.viewAllResults) this.viewAllResults.classList.add('hidden');
        this.searchReultsExample.forEach(element => {
          element.classList.remove('hidden');
          element.classList.add('flex');
        });
        
        // CRITICAL: Keep open for keyboard navigation
        if (this.input) this.input.setAttribute('aria-expanded', 'true');
        this.setAttribute('open', 'true');
        this.isOpen = true;
        return;
      } else {
        this.close(true);
        return;
      }
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (this.input?.closest('.menu-drawer')) {
      const menuDrawer = this.input.closest('.menu-drawer');
      menuDrawer.querySelector('.menu-drawer__inner-container').classList.add('show-search-items');
    }
    if (!currentSearchTerm.length) return;
    
    if (this.searchTerm !== currentSearchTerm) {
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    // Don't close if in search modal with recommendations
    setTimeout(() => {
      if (this.contains(document.activeElement)) return;
      
      const isInSearchModal = this.searchModal && this.searchModal.id === 'modal-search';
      
      // Check if focus moved to an element inside the modal (close button, recommendations)
      const modalElement = document.querySelector('#modal-search');
      const focusIsInModal = modalElement && modalElement.contains(document.activeElement);
      
      if (isInSearchModal && focusIsInModal && !this.searchTerm.length) {
        // Don't close - user is navigating within modal
        return;
      }
      
      this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) {
      const isInSearchModal = this.searchModal && this.searchModal.id === 'modal-search';
      if (!isInSearchModal) {
        this.close(true);
      }
    }
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }

    if (event.code == 'NumpadEnter' || event.key === 'Enter') {
      if((document.activeElement && document.activeElement.id === 'form-search-on-search-modal') && this.searchTerm) {
        event.preventDefault();
        const searchURL = `/search?q=${this.searchTerm}`;
        window.location.href = searchURL;
      }
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, 'g'))?.length > 1) {
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    if (this.input) this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(`${routes.predictive_search_url}?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search&resources[limit]=${this.numberOfResults}`, {
      signal: this.abortController.signal,
    })
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);
      })
      .catch((error) => {
        if (error?.code === 20) {
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    
    if (this.viewAllResults) {
      this.viewAllResults.setAttribute('href', `/search?q=${this.searchTerm}`)
      this.viewAllResults.classList.remove('hidden');
    }

    this.searchReultsExample.forEach(element => {
      element.classList.add('hidden');
      element.classList.remove('flex');
    });

    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]')?.textContent || '');
  }

  getResultsMaxHeight() {
    this.resultsMaxHeight =
      window.innerHeight - document.querySelector('.section-header')?.getBoundingClientRect().bottom;
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    if (this.input) this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  clearResults() {
    this.abortController.abort();
    this.abortController = new AbortController();
    if (this.predictiveSearchResults) this.predictiveSearchResults.innerHTML = this.initialResultsMarkup;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      if (this.input) this.input.value = '';
      this.clearResults();
      this.removeAttribute('results');
      if (this.viewAllResults) this.viewAllResults.classList.add('hidden');
      this.searchReultsExample.forEach(element => {
        element.classList.remove('hidden');
        element.classList.add('flex');
      });
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    if (this.input) this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    if (this.input) this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);